import React, { useState, useMemo } from "react";
import AppLayout from "../components/AppLayout";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";
import { Card, CardContent } from "../components/ui/card";
import { Label } from "../components/ui/label";
import {
    ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
    BarChart, Bar, PieChart, Pie, Cell,
} from "recharts";
import { motion, AnimatePresence } from "framer-motion";
import { Palmtree, ShieldCheck, Landmark, Flame } from "lucide-react";
import CountUp from "../components/CountUp";
import SliderInput from "../components/SliderInput";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";

const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);
const fmt2 = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const GOAL_TABS = [
    { key: "retirement", icon: Landmark, tkey: "goal_tab_retirement" },
    { key: "vacation", icon: Palmtree, tkey: "goal_tab_vacation" },
    { key: "emergency", icon: ShieldCheck, tkey: "goal_tab_emergency" },
    { key: "debt", icon: Flame, tkey: "goal_tab_debt" },
];

// ---- shared UI ----
const ResultTile = ({ label, numeric, format = (n) => n.toFixed(0), color = "text-[hsl(var(--foreground))]", big = false }) => (
    <div>
        <div className="text-[10px] font-bold tracking-[0.2em] text-[hsl(var(--muted-foreground))] uppercase">{label}</div>
        <div className={`font-display font-extrabold tabular-nums ${big ? "text-3xl md:text-4xl" : "text-xl md:text-2xl"} tracking-tight ${color} mt-1`}>
            <CountUp value={numeric} format={format} />
        </div>
    </div>
);

// ==================== Retirement ====================
const RetirementCalc = ({ language }) => {
    const [currentAge, setCurrentAge] = useState(30);
    const [retireAge, setRetireAge] = useState(65);
    const [savings, setSavings] = useState(15000);
    const [monthly, setMonthly] = useState(500);
    const [rate, setRate] = useState(7);
    const [annualNeed, setAnnualNeed] = useState(50000);

    const result = useMemo(() => {
        const years = Math.max(1, retireAge - currentAge);
        const monthlyRate = rate / 100 / 12;
        const n = years * 12;
        let balance = savings;
        const series = [{ age: currentAge, balance: Math.round(balance) }];
        for (let i = 1; i <= n; i++) {
            balance = balance * (1 + monthlyRate) + monthly;
            if (i % 12 === 0) {
                series.push({ age: currentAge + i / 12, balance: Math.round(balance) });
            }
        }
        // 25x annual spend rule (safe withdrawal 4%)
        const target = annualNeed * 25;
        const gap = balance - target;
        const covered = Math.min(100, (balance / target) * 100);
        return { years, projected: balance, target, gap, covered, series };
    }, [currentAge, retireAge, savings, monthly, rate, annualNeed]);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="rounded-3xl border-[hsl(var(--border))] shadow-sm">
                <CardContent className="p-6 md:p-8 space-y-6">
                    <div>
                        <div className="text-[11px] font-bold tracking-[0.24em] text-[hsl(var(--muted-foreground))] uppercase">
                            {language === "es" ? "PARÁMETROS · RETIRO" : "PARAMETERS · RETIREMENT"}
                        </div>
                        <h3 className="font-display font-bold text-2xl mt-1">
                            {language === "es" ? "Planea tu tranquilidad" : "Plan your peace of mind"}
                        </h3>
                    </div>
                    <SliderInput label={t(language, "retire_current_age")} value={currentAge} onChange={setCurrentAge} min={18} max={70} step={1} unit={language === "es" ? " años" : " yrs"} />
                    <SliderInput label={t(language, "retire_target_age")} value={retireAge} onChange={setRetireAge} min={40} max={80} step={1} unit={language === "es" ? " años" : " yrs"} />
                    <SliderInput label={t(language, "retire_current_savings")} value={savings} onChange={setSavings} min={0} max={500000} step={1} format={fmt} />
                    <SliderInput label={t(language, "retire_monthly")} value={monthly} onChange={setMonthly} min={0} max={5000} step={1} format={fmt} />
                    <SliderInput label={t(language, "retire_return")} value={rate} onChange={setRate} min={1} max={15} step={0.5} unit="%" />
                    <SliderInput label={t(language, "retire_needed_annual")} value={annualNeed} onChange={setAnnualNeed} min={10000} max={200000} step={1} format={fmt} />
                </CardContent>
            </Card>

            <Card className="rounded-3xl border-[hsl(var(--border))] shadow-sm overflow-hidden">
                <CardContent className="p-6 md:p-8">
                    <div className="text-[11px] font-bold tracking-[0.24em] text-[hsl(var(--muted-foreground))] uppercase mb-4">
                        {language === "es" ? `EN ${result.years} AÑOS` : `IN ${result.years} YEARS`}
                    </div>
                    <ResultTile label={t(language, "retire_result")} numeric={result.projected} format={fmt} color="text-[hsl(var(--primary))]" big />
                    <div className="grid grid-cols-2 gap-4 mt-6">
                        <ResultTile
                            label={result.gap >= 0 ? (language === "es" ? "Excedente" : "Surplus") : t(language, "retire_gap")}
                            numeric={Math.abs(result.gap)}
                            format={fmt}
                            color={result.gap >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-[#B85A3E] dark:text-[#F9C77E]"}
                        />
                        <ResultTile label={t(language, "retire_covered")} numeric={result.covered} format={(n) => `${n.toFixed(0)}%`} color="text-[hsl(var(--foreground))]" />
                    </div>
                    <motion.div
                        key={`${currentAge}-${retireAge}-${savings}-${monthly}-${rate}`}
                        initial={{ opacity: 0, y: 16, rotateX: -8 }}
                        animate={{ opacity: 1, y: 0, rotateX: 0 }}
                        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                        className="mt-6 h-56"
                        style={{ perspective: 1000 }}
                    >
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={result.series}>
                                <defs>
                                    <linearGradient id="retireGrad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#7DD3B0" stopOpacity={0.7} />
                                        <stop offset="100%" stopColor="#7DD3B0" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="hsl(var(--border))" />
                                <XAxis dataKey="age" tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}`} />
                                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
                                <Tooltip formatter={(v) => fmt(v)} labelFormatter={(l) => (language === "es" ? `Edad ${l}` : `Age ${l}`)} />
                                <Area type="monotone" dataKey="balance" stroke="#7DD3B0" strokeWidth={2.5} fill="url(#retireGrad)" isAnimationActive animationDuration={1100} animationEasing="ease-out" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </motion.div>
                    <p className="mt-3 text-xs text-[hsl(var(--muted-foreground))]">
                        {language === "es"
                            ? "Basado en la regla del 25x (tasa de retiro segura del 4% anual)."
                            : "Based on the 25x rule (a 4% safe annual withdrawal rate)."}
                    </p>
                </CardContent>
            </Card>
        </div>
    );
};

// ==================== Vacation ====================
const VacationCalc = ({ language }) => {
    const [dest, setDest] = useState(language === "es" ? "Playa del Carmen" : "Beach Getaway");
    const [cost, setCost] = useState(3000);
    const [current, setCurrent] = useState(400);
    const [months, setMonths] = useState(6);

    const result = useMemo(() => {
        const gap = Math.max(0, cost - current);
        const monthlyNeeded = months > 0 ? gap / months : gap;
        const progress = cost > 0 ? Math.min(100, (current / cost) * 100) : 0;
        const series = [];
        let bal = current;
        for (let m = 0; m <= months; m++) {
            series.push({ month: m, saved: Math.round(bal), goal: cost });
            bal += monthlyNeeded;
        }
        return { gap, monthlyNeeded, progress, series };
    }, [cost, current, months]);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="rounded-3xl border-[hsl(var(--border))] shadow-sm">
                <CardContent className="p-6 md:p-8 space-y-6">
                    <div>
                        <div className="text-[11px] font-bold tracking-[0.24em] text-[hsl(var(--muted-foreground))] uppercase">
                            {language === "es" ? "PARÁMETROS · VIAJE" : "PARAMETERS · TRIP"}
                        </div>
                        <h3 className="font-display font-bold text-2xl mt-1">
                            {language === "es" ? "Tu escape planeado" : "Your planned escape"}
                        </h3>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold tracking-[0.14em] text-[hsl(var(--muted-foreground))] uppercase mb-2 block">
                            {t(language, "vac_destination")}
                        </Label>
                        <input
                            type="text"
                            value={dest}
                            onChange={(e) => setDest(e.target.value)}
                            data-testid="vacation-destination"
                            className="w-full h-11 px-4 rounded-full bg-[hsl(var(--secondary))]/50 border border-transparent focus:border-[hsl(var(--primary))] outline-none text-sm"
                        />
                    </div>
                    <SliderInput label={t(language, "vac_cost")} value={cost} onChange={setCost} min={500} max={30000} step={1} format={fmt} />
                    <SliderInput label={t(language, "vac_current")} value={current} onChange={setCurrent} min={0} max={30000} step={1} format={fmt} />
                    <SliderInput label={t(language, "vac_months")} value={months} onChange={setMonths} min={1} max={36} step={1} unit={language === "es" ? " meses" : " mos"} />
                </CardContent>
            </Card>

            <Card className="rounded-3xl border-[hsl(var(--border))] shadow-sm overflow-hidden">
                <CardContent className="p-6 md:p-8">
                    <div className="text-[11px] font-bold tracking-[0.24em] text-[hsl(var(--muted-foreground))] uppercase mb-4">
                        {dest.toUpperCase()}
                    </div>
                    <ResultTile label={t(language, "vac_monthly_needed")} numeric={result.monthlyNeeded} format={fmt2} color="text-[hsl(var(--primary))]" big />
                    <div className="grid grid-cols-2 gap-4 mt-6">
                        <ResultTile label={t(language, "vac_progress")} numeric={result.progress} format={(n) => `${n.toFixed(0)}%`} color="text-emerald-600" />
                        <ResultTile label={language === "es" ? "Falta" : "Remaining"} numeric={result.gap} format={fmt} color="text-[#B85A3E] dark:text-[#F9C77E]" />
                    </div>
                    {/* Modern progress bar — gradient fill, shimmer sheen, milestone chips inline */}
                    <div className="mt-6" data-testid="vacation-progress">
                        <div className="flex items-center justify-between mb-2">
                            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">
                                {language === "es" ? "Progreso al destino" : "Progress to destination"}
                            </div>
                            <div className="text-sm font-extrabold tabular-nums text-[hsl(var(--foreground))]">
                                {`${result.progress.toFixed(0)}%`}
                            </div>
                        </div>
                        <div className="relative w-full h-5 rounded-full bg-[hsl(var(--secondary))] overflow-hidden ring-1 ring-[hsl(var(--border))]">
                            {/* filled bar */}
                            <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${result.progress}%` }}
                                transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                                className="absolute inset-y-0 left-0 rounded-full"
                                style={{
                                    background: "linear-gradient(90deg, #7DD3B0 0%, #A6E4C5 45%, #F9C77E 100%)",
                                    boxShadow: "0 0 24px -6px #7DD3B0, inset 0 -2px 0 rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.35)",
                                }}
                            />
                            {/* shimmer sheen */}
                            <motion.div
                                initial={{ x: "-100%" }}
                                animate={{ x: "220%" }}
                                transition={{ duration: 2.6, ease: "linear", repeat: Infinity, repeatDelay: 1.2 }}
                                className="absolute inset-y-0 w-1/3 pointer-events-none"
                                style={{
                                    background:
                                        "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.55) 50%, rgba(255,255,255,0) 100%)",
                                    mixBlendMode: "overlay",
                                    filter: "blur(2px)",
                                }}
                            />
                            {/* milestone tick chips */}
                            {[25, 50, 75].map((m) => (
                                <div
                                    key={m}
                                    className="absolute top-0 bottom-0 w-px bg-[hsl(var(--foreground))]/25"
                                    style={{ left: `${m}%` }}
                                />
                            ))}
                            {/* palm marker at head of bar */}
                            <motion.div
                                initial={{ left: 0 }}
                                animate={{ left: `calc(${Math.min(100, Math.max(0, result.progress))}% - 12px)` }}
                                transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                                className="absolute top-1/2 -translate-y-1/2 w-6 h-6 rounded-full grid place-items-center pointer-events-none"
                                style={{
                                    background: "#FFFFFF",
                                    boxShadow: "0 4px 14px -3px rgba(46,196,182,0.6)",
                                    border: "2px solid #7DD3B0",
                                }}
                            >
                                <Palmtree className="w-3 h-3 text-[#7DD3B0]" />
                            </motion.div>
                        </div>
                        {/* milestone chips row */}
                        <div className="mt-2 flex justify-between text-[10px] font-bold tracking-wider uppercase text-[hsl(var(--muted-foreground))]">
                            <span className={result.progress >= 25 ? "text-[#7DD3B0]" : ""}>25%</span>
                            <span className={result.progress >= 50 ? "text-[#7DD3B0]" : ""}>50%</span>
                            <span className={result.progress >= 75 ? "text-[#7DD3B0]" : ""}>75%</span>
                            <span className={`inline-flex items-center gap-1 ${result.progress >= 100 ? "text-[#F9C77E]" : ""}`}>
                                <Palmtree className="w-3 h-3" />
                                {language === "es" ? "Destino" : "There!"}
                            </span>
                        </div>
                    </div>
                    <motion.div
                        key={`${cost}-${current}-${months}`}
                        initial={{ opacity: 0, y: 18, rotateX: -6 }}
                        animate={{ opacity: 1, y: 0, rotateX: 0 }}
                        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                        className="mt-6 h-48"
                        style={{ perspective: 1000 }}
                    >
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={result.series} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="0" vertical={false} stroke="hsl(var(--border))" strokeOpacity={0.35} />
                                <XAxis
                                    dataKey="month"
                                    tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                                    tickFormatter={(v) => `M${v}`}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <YAxis
                                    tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                                    tickFormatter={(v) => `$${Math.round(v / 1000)}k`}
                                    axisLine={false}
                                    tickLine={false}
                                    width={44}
                                />
                                <Tooltip
                                    cursor={{ fill: "transparent" }}
                                    wrapperStyle={{ outline: "none" }}
                                    contentStyle={{
                                        background: "rgba(15, 23, 40, 0.92)",
                                        border: "1px solid rgba(46,196,182,0.35)",
                                        borderRadius: 12,
                                        color: "#FFFFFF",
                                        boxShadow: "0 8px 30px -6px rgba(0,0,0,0.45)",
                                    }}
                                    labelStyle={{ color: "#CBD5E1" }}
                                    itemStyle={{ color: "#7DD3B0" }}
                                    formatter={(v) => fmt(v)}
                                />
                                <Bar dataKey="saved" fill="#7DD3B0" radius={[8, 8, 0, 0]} isAnimationActive animationDuration={900} animationEasing="ease-out" />
                            </BarChart>
                        </ResponsiveContainer>
                    </motion.div>
                </CardContent>
            </Card>
        </div>
    );
};

// ==================== Emergency Fund ====================
const EmergencyCalc = ({ language }) => {
    const [monthlyExp, setMonthlyExp] = useState(2500);
    const [months, setMonths] = useState(6);
    const [current, setCurrent] = useState(3000);

    const result = useMemo(() => {
        const target = monthlyExp * months;
        const gap = Math.max(0, target - current);
        const covered = target > 0 ? Math.min(100, (current / target) * 100) : 0;
        const monthsCoveredNow = monthlyExp > 0 ? current / monthlyExp : 0;
        const donut = [
            { name: language === "es" ? "Ahorrado" : "Saved", value: Math.min(current, target), color: "#7DD3B0" },
            { name: language === "es" ? "Falta" : "Remaining", value: gap, color: "#EEE4D8" },
        ];
        return { target, gap, covered, monthsCoveredNow, donut };
    }, [monthlyExp, months, current, language]);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="rounded-3xl border-[hsl(var(--border))] shadow-sm">
                <CardContent className="p-6 md:p-8 space-y-6">
                    <div>
                        <div className="text-[11px] font-bold tracking-[0.24em] text-[hsl(var(--muted-foreground))] uppercase">
                            {language === "es" ? "PARÁMETROS · EMERGENCIA" : "PARAMETERS · EMERGENCY"}
                        </div>
                        <h3 className="font-display font-bold text-2xl mt-1">
                            {language === "es" ? "Tu colchón de tranquilidad" : "Your safety cushion"}
                        </h3>
                    </div>
                    <SliderInput label={t(language, "emg_monthly_exp")} value={monthlyExp} onChange={setMonthlyExp} min={500} max={15000} step={1} format={fmt} />
                    <SliderInput label={t(language, "emg_months")} value={months} onChange={setMonths} min={3} max={12} step={1} unit={language === "es" ? " meses" : " mos"} />
                    <SliderInput label={t(language, "emg_current")} value={current} onChange={setCurrent} min={0} max={100000} step={1} format={fmt} />
                    <div className="text-xs text-[hsl(var(--muted-foreground))] pt-2 border-t border-[hsl(var(--border))]">
                        {language === "es"
                            ? "Regla clásica: 3–6 meses de gastos básicos. Si tus ingresos son variables, apunta a 6–12."
                            : "Classic rule: 3–6 months of core expenses. If your income is variable, aim for 6–12."}
                    </div>
                </CardContent>
            </Card>

            <Card className="rounded-3xl border-[hsl(var(--border))] shadow-sm overflow-hidden">
                <CardContent className="p-6 md:p-8">
                    <div className="text-[11px] font-bold tracking-[0.24em] text-[hsl(var(--muted-foreground))] uppercase mb-4">
                        {language === "es" ? "COBERTURA" : "COVERAGE"}
                    </div>
                    <ResultTile label={t(language, "emg_target")} numeric={result.target} format={fmt} color="text-[hsl(var(--primary))]" big />
                    <div className="grid grid-cols-2 gap-4 mt-6">
                        <ResultTile label={t(language, "emg_status")} numeric={result.covered} format={(n) => `${n.toFixed(0)}%`} color="text-emerald-600" />
                        <ResultTile label={language === "es" ? "Meses cubiertos" : "Months covered"} numeric={result.monthsCoveredNow} format={(n) => n.toFixed(1)} color="text-[hsl(var(--foreground))]" />
                    </div>
                    <motion.div
                        key={`${monthlyExp}-${months}-${current}`}
                        initial={{ opacity: 0, scale: 0.9, rotateY: -12 }}
                        animate={{ opacity: 1, scale: 1, rotateY: 0 }}
                        transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
                        className="mt-6 h-56"
                        style={{ perspective: 1000 }}
                    >
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={result.donut}
                                    dataKey="value"
                                    innerRadius="55%"
                                    outerRadius="85%"
                                    paddingAngle={2}
                                    startAngle={90}
                                    endAngle={-270}
                                    isAnimationActive
                                    animationDuration={1000}
                                    animationEasing="ease-out"
                                >
                                    {result.donut.map((d, i) => <Cell key={i} fill={d.color} />)}
                                </Pie>
                                <Tooltip formatter={(v) => fmt(v)} />
                            </PieChart>
                        </ResponsiveContainer>
                    </motion.div>
                </CardContent>
            </Card>
        </div>
    );
};

// ==================== Page ====================

// ==================== Debt Payoff Calculator ====================
const DebtPayoffCalc = ({ language }) => {
    const [balance, setBalance] = useState(5000);
    const [apr, setApr] = useState(19.99);
    const [payment, setPayment] = useState(250);

    // Simulate month-by-month payoff schedule locally so slider interaction is instant.
    const result = useMemo(() => {
        const monthlyRate = apr / 100 / 12;
        const minInterest = balance * monthlyRate;
        if (payment <= minInterest) {
            return { warning: true, months: Infinity, series: [], totalInterest: 0, payoffDate: null };
        }
        let bal = balance;
        let totalInterest = 0;
        const series = [{ month: 0, balance: Math.round(bal), paid: 0 }];
        let m = 0;
        while (bal > 0 && m < 480) {
            m += 1;
            const interest = bal * monthlyRate;
            const principal = Math.min(payment - interest, bal);
            totalInterest += interest;
            bal = Math.max(0, bal - principal);
            series.push({ month: m, balance: Math.round(bal), paid: Math.round(balance - bal) });
        }
        const now = new Date();
        const payoff = new Date(now.getFullYear(), now.getMonth() + m, 1);
        return {
            warning: false,
            months: m,
            series,
            totalInterest,
            payoffDate: payoff.toLocaleString(language === "es" ? "es-ES" : "en-US", { month: "short", year: "numeric" }),
            monthlyPayment: payment,
        };
    }, [balance, apr, payment, language]);

    const progress = result.warning ? 0 : Math.min(100, ((balance - (result.series.at(-1)?.balance || 0)) / balance) * 100);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="rounded-3xl border-[hsl(var(--border))] shadow-sm">
                <CardContent className="p-6 md:p-8 space-y-6">
                    <div>
                        <div className="text-[11px] font-bold tracking-[0.24em] text-[hsl(var(--muted-foreground))] uppercase">
                            {language === "es" ? "PARÁMETROS · DEUDA" : "PARAMETERS · DEBT"}
                        </div>
                        <h3 className="font-display font-bold text-2xl mt-1">
                            {language === "es" ? "Tu plan de pago" : "Your payoff plan"}
                        </h3>
                    </div>
                    <SliderInput label={t(language, "settings_debt_balance")} value={balance} onChange={setBalance} min={100} max={100000} step={1} format={fmt} testId="debt-balance" />
                    <SliderInput label={t(language, "settings_debt_apr")} value={apr} onChange={setApr} min={0} max={35} step={0.1} unit="%" testId="debt-apr" />
                    <SliderInput label={t(language, "settings_debt_payment")} value={payment} onChange={setPayment} min={10} max={5000} step={1} format={fmt} testId="debt-payment" />
                </CardContent>
            </Card>

            <Card className="rounded-3xl border-[hsl(var(--border))] shadow-sm overflow-hidden" data-testid="debt-payoff-card">
                <CardContent className="p-6 md:p-8">
                    <div className="text-[11px] font-bold tracking-[0.24em] text-[hsl(var(--muted-foreground))] uppercase mb-4">
                        {language === "es" ? "SALIDA DE DEUDA" : "DEBT FREEDOM"}
                    </div>
                    {result.warning ? (
                        <div className="rounded-2xl bg-[hsl(var(--destructive))]/10 p-5 text-[hsl(var(--destructive))] font-medium text-sm" data-testid="debt-warning">
                            {language === "es"
                                ? "Tu pago mensual no cubre ni los intereses. Auméntalo para poder salir de esta deuda."
                                : "Your monthly payment doesn't cover the interest. Raise it to make progress."}
                        </div>
                    ) : (
                        <>
                            <ResultTile
                                label={t(language, "settings_debt_months")}
                                numeric={result.months}
                                format={(n) => `${Math.round(n)}`}
                                color="text-[hsl(var(--primary))]"
                                big
                            />
                            <div className="grid grid-cols-2 gap-4 mt-6">
                                <ResultTile
                                    label={t(language, "settings_debt_payoff_date")}
                                    numeric={0}
                                    format={() => result.payoffDate || ""}
                                    color="text-[hsl(var(--foreground))]"
                                />
                                <ResultTile
                                    label={t(language, "settings_debt_interest")}
                                    numeric={result.totalInterest}
                                    format={fmt}
                                    color="text-[#F97C7C]"
                                />
                            </div>

                            {/* Payoff progress bar — matches Vacation style */}
                            <div className="mt-6" data-testid="debt-progress">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">
                                        {language === "es" ? "Progreso" : "Progress"}
                                    </div>
                                    <div className="text-sm font-extrabold tabular-nums text-[hsl(var(--foreground))]">
                                        {progress.toFixed(0)}%
                                    </div>
                                </div>
                                <div className="relative w-full h-5 rounded-full bg-[hsl(var(--secondary))] overflow-hidden ring-1 ring-[hsl(var(--border))]">
                                    <motion.div
                                        initial={{ width: 0 }}
                                        animate={{ width: `${progress}%` }}
                                        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                                        className="absolute inset-y-0 left-0 rounded-full"
                                        style={{
                                            background: "linear-gradient(90deg, #F97C7C 0%, #F9AE7C 55%, #7DD3B0 100%)",
                                            boxShadow: "0 0 24px -6px #7DD3B0, inset 0 -2px 0 rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.35)",
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Balance decline chart */}
                            <div className="h-56 mt-6" data-testid="debt-chart">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={result.series} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
                                        <defs>
                                            <linearGradient id="debt-area" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor="#F97C7C" stopOpacity={0.55} />
                                                <stop offset="100%" stopColor="#F97C7C" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="0" vertical={false} stroke="hsl(var(--border))" strokeOpacity={0.4} />
                                        <XAxis dataKey="month" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} tickFormatter={(v) => `M${v}`} />
                                        <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} width={44} />
                                        <Tooltip
                                            wrapperStyle={{ outline: "none" }}
                                            contentStyle={{
                                                background: "rgba(15, 23, 40, 0.92)",
                                                border: "1px solid rgba(46,196,182,0.35)",
                                                borderRadius: 12,
                                                color: "#FFFFFF",
                                            }}
                                            labelStyle={{ color: "#CBD5E1" }}
                                            itemStyle={{ color: "#7DD3B0" }}
                                            formatter={(v) => fmt(v)}
                                        />
                                        <Area type="monotone" dataKey="balance" stroke="#F97C7C" strokeWidth={2.4} fill="url(#debt-area)" isAnimationActive animationDuration={900} />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};


const Goals = () => {
    const { language } = useApp();
    const [tab, setTab] = useState("retirement");

    return (
        <AppLayout title={t(language, "nav_goals")}>
            {/* Hero */}
            <div className="mb-6 text-center">
                <div className="inline-block text-[11px] font-bold tracking-[0.24em] text-[hsl(var(--muted-foreground))] uppercase border-t border-[hsl(var(--border))] pt-1">
                    {t(language, "goals_hero_eyebrow")}
                </div>
                <h1 className="font-display font-extrabold text-[hsl(var(--foreground))] text-4xl md:text-5xl leading-[1.05] tracking-[-0.02em] mt-2">
                    {t(language, "goals_hero_title")}
                </h1>
                <p className="mt-3 text-[hsl(var(--muted-foreground))] max-w-2xl mx-auto">
                    {t(language, "goals_hero_desc")}
                </p>
            </div>

            {/* Tabs */}
            <div className="mb-6 flex flex-wrap justify-center gap-2" data-testid="goals-tabs">
                {GOAL_TABS.map((g) => {
                    const Icon = g.icon;
                    const active = tab === g.key;
                    return (
                        <button
                            key={g.key}
                            data-testid={`goals-tab-${g.key}`}
                            onClick={() => setTab(g.key)}
                            className={`inline-flex items-center gap-2 px-4 py-2.5 pill text-sm font-semibold transition-all ${
                                active
                                    ? "bg-[hsl(var(--foreground))] text-[hsl(var(--background))] shadow-md"
                                    : "bg-[hsl(var(--card))] text-[hsl(var(--foreground))] border border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]"
                            }`}
                        >
                            <Icon className="w-4 h-4" />
                            {t(language, g.tkey)}
                        </button>
                    );
                })}
            </div>

            <AnimatePresence mode="wait">
                <motion.div
                    key={tab}
                    initial={{ opacity: 0, y: 14, scale: 0.985 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                >
                    {tab === "retirement" && <RetirementCalc language={language} />}
                    {tab === "vacation" && <VacationCalc language={language} />}
                    {tab === "emergency" && <EmergencyCalc language={language} />}
                    {tab === "debt" && <DebtPayoffCalc language={language} />}
                </motion.div>
            </AnimatePresence>
        </AppLayout>
    );
};

export default Goals;
